/**
 * Fluent helper for authoring tile-grid tracks by hand.
 */
import { Surface, Shape, type Tile, type Track, type TrackCategory, type CourseDifficulty } from '../types.js';
import { MAP_COLS, MAP_ROWS } from '../constants.js';
import { GRASS_TILE, makeTile, makeSpecial, tileCenter } from '../physics/track.js';

export class TrackBuilder {
  private tiles: Tile[];
  private teeCol = 2;
  private teeRow = 12;
  private holeCol = 46;
  private holeRow = 12;

  constructor() {
    this.tiles = new Array(MAP_COLS * MAP_ROWS).fill(null).map(() => ({ ...GRASS_TILE }));
  }

  private idx(col: number, row: number): number {
    return row * MAP_COLS + col;
  }

  private inBounds(col: number, row: number): boolean {
    return col >= 0 && col < MAP_COLS && row >= 0 && row < MAP_ROWS;
  }

  set(col: number, row: number, tile: Tile): this {
    if (this.inBounds(col, row)) this.tiles[this.idx(col, row)] = { ...tile };
    return this;
  }

  /** Solid block border around the field. */
  border(): this {
    for (let c = 0; c < MAP_COLS; c++) {
      this.set(c, 0, makeTile(Surface.Block, Surface.Block, Shape.Blank));
      this.set(c, MAP_ROWS - 1, makeTile(Surface.Block, Surface.Block, Shape.Blank));
    }
    for (let r = 0; r < MAP_ROWS; r++) {
      this.set(0, r, makeTile(Surface.Block, Surface.Block, Shape.Blank));
      this.set(MAP_COLS - 1, r, makeTile(Surface.Block, Surface.Block, Shape.Blank));
    }
    return this;
  }

  /** Fill a rectangle of solid wall blocks. */
  wall(col: number, row: number, w: number, h: number): this {
    for (let r = row; r < row + h; r++) for (let c = col; c < col + w; c++) this.set(c, r, makeTile(Surface.Block, Surface.Block, Shape.Blank));
    return this;
  }

  /** Fill a rectangle with a terrain element (background). */
  terrain(col: number, row: number, w: number, h: number, surface: Surface): this {
    for (let r = row; r < row + h; r++) for (let c = col; c < col + w; c++) this.set(c, r, makeTile(surface, surface, Shape.Blank));
    return this;
  }

  /** A single tile with a shaped foreground (e.g. a diagonal wall). */
  shaped(col: number, row: number, shape: Shape, fg: Surface = Surface.Block, bg: Surface = Surface.Grass): this {
    return this.set(col, row, makeTile(bg, fg, shape));
  }

  special(col: number, row: number, special: Surface, bg: Surface = Surface.Grass): this {
    return this.set(col, row, makeSpecial(special, bg));
  }

  tee(col: number, row: number): this {
    this.teeCol = col;
    this.teeRow = row;
    this.special(col, row, Surface.Start);
    return this;
  }

  hole(col: number, row: number): this {
    for (let i = 0; i < this.tiles.length; i++) {
      if (this.tiles[i].special === Surface.Hole) this.tiles[i] = { ...GRASS_TILE };
    }
    this.holeCol = col;
    this.holeRow = row;
    this.special(col, row, Surface.Hole);
    return this;
  }

  build(meta: {
    id: string;
    name: string;
    author?: string;
    par?: number;
    categories?: TrackCategory[];
    difficulty?: CourseDifficulty;
  }): Track {
    return {
      id: meta.id,
      name: meta.name,
      author: meta.author ?? 'jgolf',
      tiles: this.tiles,
      tee: tileCenter(this.teeCol, this.teeRow),
      cup: tileCenter(this.holeCol, this.holeRow),
      par: meta.par ?? 3,
      categories: meta.categories ?? ['basic'],
      difficulty: meta.difficulty ?? 'easy',
    };
  }
}
